/**
 * Colors Section - Color palette comparison
 *
 * Shows:
 * - Primary colors
 * - Semantic colors (success, warning, error)
 * - Gray scale
 * - Current vs richer/more saturated colors
 */
export default function ColorsSection({ theme }) {
  const isDark = theme === 'dark';

  const colorSets = {
    primary: [
      { label: 'Blue 400', current: 'bg-blue-400', ios: 'bg-blue-500' },
      { label: 'Blue 500', current: 'bg-blue-500', ios: 'bg-blue-600' },
      { label: 'Blue 600', current: 'bg-blue-600', ios: 'bg-blue-700' },
    ],
    semantic: [
      { label: 'Success', current: 'bg-green-500', ios: 'bg-green-600' },
      { label: 'Warning', current: 'bg-amber-500', ios: 'bg-amber-600' },
      { label: 'Error', current: 'bg-red-500', ios: 'bg-red-600' },
    ],
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Color Palette Comparison
        </h2>
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Current colors vs richer, more saturated iOS-style palette
        </p>
      </div>

      {/* Primary Colors */}
      <div>
        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Primary Colors
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className={`text-xs font-semibold uppercase tracking-wider mb-4 ${
              isDark ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Current Design
            </div>
            <div className="space-y-2">
              {colorSets.primary.map(color => (
                <div key={color.label} className="flex items-center gap-3">
                  <div className={`w-16 h-16 ${color.current} rounded-lg shadow-sm`} />
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {color.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className={`text-xs font-bold uppercase tracking-wider mb-4 ${
              isDark ? 'text-blue-400' : 'text-blue-600'
            }`}>
              iOS-Style (Enhanced)
            </div>
            <div className="space-y-2">
              {colorSets.primary.map(color => (
                <div key={color.label} className="flex items-center gap-3">
                  <div className={`w-16 h-16 ${color.ios} rounded-lg shadow-lg`} />
                  <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    {color.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Semantic Colors */}
      <div>
        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Semantic Colors
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="space-y-2">
              {colorSets.semantic.map(color => (
                <div key={color.label} className="flex items-center gap-3">
                  <div className={`w-16 h-16 ${color.current} rounded-lg shadow-sm`} />
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {color.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="space-y-2">
              {colorSets.semantic.map(color => (
                <div key={color.label} className="flex items-center gap-3">
                  <div className={`w-16 h-16 ${color.ios} rounded-lg shadow-lg`} />
                  <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    {color.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Color in Context */}
      <div>
        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Colors in Context
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="px-4 py-3 bg-blue-100 text-blue-800 rounded-lg">
              Info message with current colors
            </div>
            <div className="px-4 py-3 bg-green-100 text-green-800 rounded-lg">
              Success message with current colors
            </div>
            <div className="px-4 py-3 bg-amber-100 text-amber-800 rounded-lg">
              Warning message with current colors
            </div>
          </div>

          <div className="space-y-3">
            <div className="px-4 py-3 bg-blue-500 text-white rounded-lg font-semibold shadow-md">
              Info message with iOS-style colors
            </div>
            <div className="px-4 py-3 bg-green-600 text-white rounded-lg font-semibold shadow-md">
              Success message with iOS-style colors
            </div>
            <div className="px-4 py-3 bg-amber-600 text-white rounded-lg font-semibold shadow-md">
              Warning message with iOS-style colors
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
